import { useRef } from 'react'
import { connect } from 'react-redux'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { InformationCircleMiniIcon } from '@calendly/ui/components/icons'
const textColorLevel2 = 'var(--text-color-level2, rgba(26, 26, 26, 0.61))'

import { currentUserSelector } from 'selectors/user'
import { useBooleanState } from 'hooks/useBooleanState'
import { useI18n } from 'locales'
import { useGetCalendarAccountsQuery } from 'root-api'
import {
  MutualAvailabilitySignUpBox,
  MutualAvailabilitySignUpHeader,
  SignUpInnerWrapper,
  useMutualAvailabilitySignupExperiment
} from 'components/mutual_availability_signup'
import { GoogleSignUpButton, MicrosoftSignUpButton } from 'components/sign_up_buttons'

import { useMutualAvailabilityEnabled } from './use_mutual_availability_enabled'
import Popup from './popup'
import {
  Panel,
  Header,
  ClickableHeader,
  InfoIconContainer,
  Avatar,
  Title,
  Content,
  ScrollContainer,
  PretendEventWarning,
  MutualAvailabilitySidebarWrapper
} from './styles'
import ErrorWarning from './error_warning'
import { useContainerPosition } from './use_container_position'
import EventList from './invitee_event_list'
import CalendarsLine from './calendars_line'

const MutualAvailabilityExperimentDecider = ({ isEnabled, children }) => {
  const { isSignUpWithGoogle, isSignUpWithMicrosoft } = useMutualAvailabilitySignupExperiment()
  const isPhone = useDeviceType('isPhone')

  if (!isEnabled && isPhone && (isSignUpWithGoogle || isSignUpWithMicrosoft)) {
    return (
      <SignUpInnerWrapper>
        <MutualAvailabilitySignUpHeader />
        <GoogleSignUpButton width={325} />
        <MicrosoftSignUpButton />
      </SignUpInnerWrapper>
    )
  }
  if (!isEnabled && !isPhone && (isSignUpWithGoogle || isSignUpWithMicrosoft)) {
    return (
      <MutualAvailabilitySidebarWrapper>
        <MutualAvailabilitySignUpBox />
      </MutualAvailabilitySidebarWrapper>
    )
  }

  if (!isEnabled) return null

  return children
}

const MutualAvailabilitySidebar = ({ avatarUrl, inviteePublisherError, locale, isPretending }) => {
  const { i18n } = useI18n()
  const isEnabled = useMutualAvailabilityEnabled()
  const contentRef = useRef()
  const screenPosition = useContainerPosition(contentRef)
  const [isPopupOpen, openPopup, closePopup] = useBooleanState(false)
  const isPhone = useDeviceType('isPhone')
  const { isLoading, data: calendarAccounts } = useGetCalendarAccountsQuery(undefined, { skip: !isEnabled })

  if (isLoading) return null

  const hasCalendarConnected = !!calendarAccounts?.length
  const HeaderComponent = hasCalendarConnected ? ClickableHeader : Header

  if (isPhone && isPretending) {
    return (
      <PretendEventWarning>
        Calendly Admins can not see users' calendar events while in pretend mode.
      </PretendEventWarning>
    )
  }

  if (isPhone && !isPretending) {
    return (
      <MutualAvailabilityExperimentDecider isEnabled={isEnabled}>
        <EventList containerScreenPosition={screenPosition} />
      </MutualAvailabilityExperimentDecider>
    )
  }

  return (
    <MutualAvailabilityExperimentDecider isEnabled={isEnabled}>
      <Panel>
        <HeaderComponent onClick={hasCalendarConnected ? openPopup : null}>
          {hasCalendarConnected && (
            <InfoIconContainer>
              <InformationCircleMiniIcon color={textColorLevel2} size="x-large" />
            </InfoIconContainer>  
          )}
          {avatarUrl && <Avatar src={avatarUrl} alt={i18n.t('booking.avatar', { locale })} />}
          <Title>{i18n.t('publisher.mutual_availability.header.schedule_from', { locale })}</Title>
          <CalendarsLine />
        </HeaderComponent>
        {isPretending
          ? (
            <PretendEventWarning>
              Calendly Admins can not see users' calendar events while in pretend mode.
            </PretendEventWarning>
          ) : (
            <Content ref={contentRef}>
              <ScrollContainer>
                {
                    inviteePublisherError
                      ? <ErrorWarning />
                      : <EventList containerScreenPosition={screenPosition} />
                  }
              </ScrollContainer>
            </Content>
          )}
        {isPopupOpen && <Popup onClose={closePopup} />}
      </Panel>
    </MutualAvailabilityExperimentDecider>
  )
}

const mapStateToProps = (state) => {
  const currentUser = currentUserSelector(state)

  return {
    avatarUrl: currentUser && currentUser.avatar_url,
    locale: currentUser && currentUser.locale,
    inviteePublisherError: state.calendar.inviteePublisherError,
    isPretending: currentUser && currentUser.is_pretending
  }
}

export const MutualAvailability = connect(mapStateToProps)(MutualAvailabilitySidebar)
